import React from 'react';
import PropTypes from 'prop-types';
import Lightbox from 'react-image-lightbox';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import moment from '../../../util/moment';
import { Field } from 'formik';
import classNames from 'classnames';
import _startCase from 'lodash.startcase';
import * as Yup from 'yup';

import imgPlaceholder from '../../../container/dashboard/img/image-placeholder.png';
import ErrorTop from '../../form/ErrorTop';
import IconCalendar from '../../icon/IconCalendar';
import { displayMoney } from '../../../util/currency';
import { incotermsList } from '../../../util/incoterms';
import { QuantitiesValidationScheme } from '../QuoteForm/helper';

const PhotoContainerStyled = styled.div`
  margin: 30px 20px 10px 0;
`;

const CheckboxStyled = styled.input`
  display: inline-flex;
`;

const CheckboxLabelStyled = styled.label`
  margin-left: 7px;
`;

const IconCalendarContainerStyled = styled.div`
  position: absolute;
  z-index: 2500;
  top: 6px;
  left: 10px;
`;

const ContainerInputCalendarStyled = styled.div`
  position: relative;
`;

class QuoteFormSignupAStep2 extends React.Component {
  static defaultProps = {
    readOnly: false,
  };

  static propTypes = {
    readOnly: PropTypes.bool,
    product: PropTypes.object.isRequired,
    onChangeFormStep: PropTypes.func.isRequired,
  };

  state = {
    photoIndex: 0,
    isLightboxOpen: false,
  };

  render() {
    const { photoIndex } = this.state;
    const photosLength = this.props.product.photosUrl.length;

    const {
      readOnly,
      product,
      values,
      errors,
      touched,
      isSubmitting,
      isValid,
      setFieldValue,
      setFieldTouched,
      setValues,
      setErrors,
      setSubmitting,
      handleSubmit,
    } = this.props;

    return (
      <div className="form-step-content form-step-2-content">
        <ErrorTop isSubmitting={isSubmitting} isValid={isValid} errors={errors} />

        <fieldset>
          {photosLength > 0 && (
            <div className="row">
              <div className="col-sm-3">
                <img
                  width={300}
                  height={300}
                  src={product.photosUrl[0] || imgPlaceholder}
                  alt={''}
                />
                {product.photosUrl.map((photoUrl, index) => {
                  return (
                    <PhotoContainerStyled
                      onClick={() => {
                        this.setState({ isLightboxOpen: true, photoIndex: index });
                      }}
                      className="col-sm-2 btn"
                      key={index}
                    >
                      <img height={50} alt="" src={photoUrl} />
                    </PhotoContainerStyled>
                  );
                })}
                {this.state.isLightboxOpen && (
                  <Lightbox
                    mainSrc={product.photosUrl[photoIndex]}
                    nextSrc={product.photosUrl[(photoIndex + 1) % photosLength]}
                    prevSrc={product.photosUrl[(photoIndex + photosLength - 1) % photosLength]}
                    onCloseRequest={() => this.setState({ isLightboxOpen: false })}
                    onMovePrevRequest={() =>
                      this.setState({
                        photoIndex: (photoIndex + photosLength - 1) % photosLength,
                      })
                    }
                    onMoveNextRequest={() =>
                      this.setState({
                        photoIndex: (photoIndex + 1) % photosLength,
                      })
                    }
                  />
                )}
              </div>
              <div className="col-sm-9 specification-col">
                <div className="heading-row properties-row">
                  <div className="row">
                    <div className="col-sm-6 landing-quote-big-heading">
                      <strong>{product.name}</strong>
                    </div>
                    <div className="col-sm-3">
                      <div className="form-group">
                        <label className="highlighted">Incoterms:</label>
                        <div className="form-value">
                          <Field
                            className="form-control narrow"
                            component="select"
                            name="termsPreference"
                            disabled={readOnly}
                          >
                            {Object.values(incotermsList)
                              .filter((incoterm) => incoterm.code !== 'standard')
                              .map((incoterm) => {
                                return (
                                  <option value={incoterm.code} key={incoterm.code}>
                                    {incoterm.label}
                                  </option>
                                );
                              })}
                          </Field>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-3">
                      <div
                        className={classNames('form-group with-datepicker', {
                          'with-error': touched.validUntil && errors.validUntil,
                        })}
                      >
                        <label className="highlighted">Quote Valid Until:</label>
                        <ContainerInputCalendarStyled>
                          <IconCalendarContainerStyled>
                            <IconCalendar width={18} height={18} color="#4d4d4d" />
                          </IconCalendarContainerStyled>
                          <DatePicker
                            name="validUntil"
                            className={'form-control'}
                            onChange={(date) => {
                              setFieldValue('validUntil', date.toISOString());
                            }}
                            onBlur={() => {
                              setFieldTouched('validUntil', true);
                            }}
                            selected={values.validUntil.length ? moment(values.validUntil) : null}
                            dateFormat="MM/DD/YYYY"
                            placeholderText="mm/dd/yyyy"
                            disabled={readOnly}
                            minDate={moment()}
                          />
                        </ContainerInputCalendarStyled>
                        {touched.validUntil &&
                          errors.validUntil && <label className="error">{errors.validUntil}</label>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="properties-row">
                  <label className="section-header">Specification</label>
                </div>
                {product.specs.map((spec, index) => (
                  <div key={index} className="properties-row">
                    <label>{_startCase(spec.name)}</label>
                    <div className="property-value">{spec.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="properties-row">
            <label className="section-header underlined">Quantities</label>
            <div className="quote-currency">
              Quote in <span className="highlighted">{product.currency}</span>
            </div>
          </div>

          <div className="properties-row quantities-row">
            <div className="row">
              <div
                className={classNames('form-group col-4 col-sm-4', {
                  'with-error': touched.minimumQuantity && errors.minimumQuantity,
                })}
              >
                <label>Quantity</label>
                <div className="form-value">
                  <input
                    className="form-control narrow"
                    type="number"
                    maxLength="15"
                    name="minimumQuantity"
                    placeholder="MOQ"
                    onChange={(event) => {
                      const v = event.target.value;
                      const data = { ...values, minimumQuantity: v };
                      Object.keys(data.requiredQuantity).forEach((i) => {
                        if (data.requiredQuantity[i].q === parseInt(v, 10)) {
                          data.requiredQuantity[i].pricePerUnit = data.pricePerUnit;
                          data.requiredQuantity[i].leadTime = data.leadTime;
                        }
                        if (data.requiredQuantity[i].q < parseInt(v, 10)) {
                          data.requiredQuantity[i].pricePerUnit = '';
                          data.requiredQuantity[i].leadTime = '';
                        }
                      });
                      setValues(data);
                    }}
                    onBlur={() => {
                      setFieldTouched('minimumQuantity', true);
                    }}
                    value={values.minimumQuantity}
                    disabled={readOnly}
                  />
                  {touched.minimumQuantity &&
                    errors.minimumQuantity && (
                      <label className="error">{errors.minimumQuantity}</label>
                    )}
                </div>
              </div>
              <div
                className={classNames('form-group col-4 col-sm-4', {
                  'with-error': touched.pricePerUnit && errors.pricePerUnit,
                })}
              >
                <label>Price per Unit</label>
                <div className="form-value">
                  <input
                    className="form-control narrow"
                    type="number"
                    name="pricePerUnit"
                    placeholder="ex. $4.56"
                    onChange={(event) => {
                      const v = event.target.value;
                      const data = { ...values, pricePerUnit: v };
                      Object.keys(data.requiredQuantity).forEach((i) => {
                        if (data.requiredQuantity[i].q === parseInt(data.minimumQuantity, 10)) {
                          data.requiredQuantity[i].pricePerUnit = v;
                        }
                      });
                      setValues(data);
                    }}
                    onBlur={() => {
                      setFieldTouched('pricePerUnit', true);
                    }}
                    value={values.pricePerUnit}
                    disabled={readOnly}
                  />
                  {touched.pricePerUnit &&
                    errors.pricePerUnit && <label className="error">{errors.pricePerUnit}</label>}
                </div>
              </div>
              <div
                className={classNames('form-group col-4 col-sm-4', {
                  'with-error': touched.leadTime && errors.leadTime,
                })}
              >
                <label>Lead Time</label>
                <div className="form-value">
                  <input
                    className="form-control narrow"
                    type="text"
                    name="leadTime"
                    placeholder="ex. 30 Days"
                    onChange={(event) => {
                      const v = event.target.value;
                      const data = { ...values, leadTime: v };
                      Object.keys(data.requiredQuantity).forEach((i) => {
                        if (data.requiredQuantity[i].q === parseInt(data.minimumQuantity, 10)) {
                          data.requiredQuantity[i].leadTime = v;
                        }
                      });
                      setValues(data);
                    }}
                    onBlur={() => {
                      setFieldTouched('leadTime', true);
                    }}
                    value={values.leadTime}
                    disabled={readOnly}
                  />
                  {touched.leadTime &&
                    errors.leadTime && <label className="error">{errors.leadTime}</label>}
                </div>
              </div>
            </div>
          </div>

          {values.requiredQuantity.map((item, index) => {
            const quantity = item.q;
            const fieldTouched =
              (touched.requiredQuantity && touched.requiredQuantity[index]) || {};
            const fieldError = (errors.requiredQuantity && errors.requiredQuantity[index]) || {};
            const fieldDisabled =
              (values.minimumQuantity && values.minimumQuantity >= quantity) || readOnly;

            return (
              <div className="properties-row quantities-row">
                <div className="row" key={index}>
                  <div className="form-group col-4 col-sm-4">
                    <div className="form-value vertically-centered required-quantity">
                      {quantity}
                    </div>
                  </div>
                  <div
                    className={classNames('form-group col-4 col-sm-4', {
                      'with-error': fieldTouched.pricePerUnit && fieldError.pricePerUnit,
                    })}
                  >
                    <div className="form-value">
                      <Field
                        className="form-control narrow"
                        type="number"
                        name={`requiredQuantity.${index}.pricePerUnit`}
                        placeholder="ex. $4.56"
                        disabled={fieldDisabled}
                      />
                      {fieldTouched.pricePerUnit &&
                        fieldError.pricePerUnit && (
                          <label className="error">{fieldError.pricePerUnit}</label>
                        )}
                    </div>
                  </div>
                  <div
                    className={classNames('form-group col-4 col-sm-4', {
                      'with-error': fieldTouched.leadTime && fieldError.leadTime,
                    })}
                  >
                    <div className="form-value">
                      <Field
                        className="form-control narrow"
                        name={`requiredQuantity.${index}.leadTime`}
                        placeholder="ex. 30 Days"
                        disabled={fieldDisabled}
                      />
                      {fieldTouched.leadTime &&
                        fieldError.leadTime && (
                          <label className="error">{fieldError.leadTime}</label>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {product.targetPrice && (
            <div data-id="quote-form-target-required-quantity">
              <div className="properties-row quantities-row">
                <div className="row">
                  <div className="col-sm-4">
                    <div
                      className={classNames('form-group', {
                        'with-error':
                          touched.targetRequiredQuantity && errors.targetRequiredQuantity,
                      })}
                    >
                      <label>Target price</label>
                      <Field
                        name="targetRequiredQuantity"
                        className="form-control"
                        placeholder="ex. 1000"
                        type="number"
                        disabled={
                          values.targetPriceCanNotReach ||
                          (values.minimumQuantity === values.targetRequiredQuantity &&
                            values.pricePerUnit === product.targetPrice) ||
                          readOnly
                        }
                        onChange={(event) => {
                          const targetRequiredQuantity = event.target.value;
                          const data = {
                            ...values,
                            targetRequiredQuantity: targetRequiredQuantity,
                          };
                          Object.keys(data.requiredQuantity).forEach((i) => {
                            if (
                              data.requiredQuantity[i].q === parseInt(targetRequiredQuantity, 10)
                            ) {
                              data.requiredQuantity[i].pricePerUnit = product.targetPrice;
                              data.requiredQuantity[i].leadTime = data.targetLeadTime;
                            } else if (
                              data.requiredQuantity[i].q < parseInt(targetRequiredQuantity, 10)
                            ) {
                              data.requiredQuantity[i].pricePerUnit = '';
                              data.requiredQuantity[i].leadTime = '';
                            }
                          });
                          if (
                            values.minimumQuantity === targetRequiredQuantity &&
                            values.pricePerUnit === product.targetPrice
                          ) {
                            data.targetLeadTime = values.leadTime;
                          }
                          setValues(data);
                        }}
                        onBlur={() => {
                          setFieldTouched('targetRequiredQuantity', true);
                        }}
                      />
                      {touched.targetRequiredQuantity &&
                        errors.targetRequiredQuantity && (
                          <label id="targetRequiredQuantity-error" className="error">
                            {errors.targetRequiredQuantity}
                          </label>
                        )}
                    </div>
                    <div className="form-group">
                      <CheckboxStyled
                        id="targetPriceCanNotReach"
                        name="targetPriceCanNotReach"
                        type="checkbox"
                        onChange={() => {
                          setFieldValue('targetPriceCanNotReach', !values.targetPriceCanNotReach);
                        }}
                        onBlur={() => {
                          setFieldTouched('targetPriceCanNotReach', true);
                        }}
                        checked={values.targetPriceCanNotReach}
                        disabled={readOnly}
                      />
                      <CheckboxLabelStyled htmlFor="targetPriceCanNotReach">
                        Select here if you cannot reach this price
                      </CheckboxLabelStyled>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div className="form-group">
                      <label>&nbsp;</label>
                      <div className="target-price-value" id="targetPrice">
                        {displayMoney(product.targetPrice, product.currency)}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div
                      className={classNames('form-group', {
                        'with-error': touched.targetLeadTime && errors.targetLeadTime,
                      })}
                    >
                      <label>&nbsp;</label>
                      <Field
                        className="form-control narrow"
                        name={`targetLeadTime`}
                        placeholder="ex. 30 Days"
                        disabled={
                          values.targetPriceCanNotReach ||
                          (values.minimumQuantity === values.targetRequiredQuantity &&
                            values.pricePerUnit === product.targetPrice) ||
                          readOnly
                        }
                        onChange={(event) => {
                          const v = event.target.value;
                          const data = { ...values, targetLeadTime: v };
                          Object.keys(data.requiredQuantity).forEach((i) => {
                            if (data.requiredQuantity[i].q === parseInt(v, 10)) {
                              data.requiredQuantity[i].leadTime = v;
                            }
                          });
                          setValues(data);
                        }}
                        onBlur={() => {
                          setFieldTouched('targetLeadTime', true);
                        }}
                      />
                      {touched.targetLeadTime &&
                        errors.targetLeadTime && (
                          <label className="error">{errors.targetLeadTime}</label>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </fieldset>

        {errors.common && <label className="error common">{errors.common}</label>}
        {!this.props.readOnly && (
          <div className="actions-bottom actions-bottom-centered">
            <button
              className="btn blue-button wide-button big-button"
              disabled={isSubmitting}
              type="button"
              onClick={() => {
                setValues({
                  ...values,
                  samplesNumber: 0,
                  samplesTotalPrice: '',
                  samplesLeadTime: '',
                  samplesShippingCost: '',
                  questions: [{ q: '', a: '' }],
                  cartonSize: '',
                  quantityPerCarton: '',
                  cartonGrossWeight: '',
                  cartonNetWeight: '',
                });
                setFieldValue('status', 'complete');
                handleSubmit(values, { props: this.props, setSubmitting, setErrors });
              }}
            >
              Send Quote and Create Account
            </button>
          </div>
        )}
      </div>
    );
  }
}

export const Step2ValidationScheme = {
  validUntil: Yup.string().required('Quote Valid Until is required'),
  ...QuantitiesValidationScheme,
};

export default QuoteFormSignupAStep2;



// WEBPACK FOOTER //
// ./src/component/quote/QuoteFormSignupAStep2/index.js